import { Pencil, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import type { AgentDevice } from '@staff-app/shared-types';
import type { LiveDeviceStatus } from '@/features/mdm-devices/hooks/useDeviceHeartbeats';
import { effectiveAgentDeviceStatus } from '../lib/device-status';
import { AgentDeviceStatusDot } from './AgentDeviceStatusDot';
import { TESTING_DEVICE_REQUIRED_MESSAGE } from '../lib/testing-account';

interface Props {
  devices: AgentDevice[];
  selectedId?: string | null;
  checkedIds: Set<string>;
  /** Live status by device UUID (open-api heartbeat); row falls back to lastHeartBeat. */
  liveStatusByUuid?: Map<string, LiveDeviceStatus>;
  onToggleCheck: (deviceId: string) => void;
  onToggleCheckAll: () => void;
  onRowClick: (device: AgentDevice) => void;
  onRemove: (device: AgentDevice) => void;
  onEditAssign: (device: AgentDevice) => void;
}

function dash(v?: string | null) {
  return v && v.length ? v : '—';
}

export function AgentDeviceTable({
  devices,
  selectedId,
  checkedIds,
  liveStatusByUuid,
  onToggleCheck,
  onToggleCheckAll,
  onRowClick,
  onRemove,
  onEditAssign,
}: Props) {
  const allChecked = devices.length > 0 && devices.every((d) => checkedIds.has(d.deviceId));
  const someChecked = devices.some((d) => checkedIds.has(d.deviceId));
  return (
    <Table data-testid="agent-device-table" className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <Checkbox
              checked={allChecked ? true : someChecked ? 'indeterminate' : false}
              onCheckedChange={() => onToggleCheckAll()}
              aria-label="Select all devices"
              data-testid="agent-device-check-all"
            />
          </TableHead>
          <TableHead className="w-16 text-center">{'Status'}</TableHead>
          <TableHead className="truncate" title="Device Name">{'Device Name'}</TableHead>
          <TableHead className="truncate" title="OS Version">{'OS Version'}</TableHead>
          <TableHead className="truncate" title="App version">{'App version'}</TableHead>
          <TableHead className="truncate" title="Whose asset">{'Whose asset'}</TableHead>
          <TableHead className="w-24 truncate" title="Assign type">{'Assign type'}</TableHead>
          <TableHead className="truncate" title="Assign name">{'Assign name'}</TableHead>
          <TableHead className="truncate" title="Assign ID">{'Assign ID'}</TableHead>
          <TableHead className="w-20" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {devices.map((d) => {
          const isSelected = selectedId === d.deviceId;
          const dev = d.device;
          return (
            <TableRow
              key={d.deviceId}
              onClick={() => onRowClick(d)}
              data-testid="agent-device-row"
              className={`${d.found ? 'cursor-pointer' : 'cursor-default'} ${isSelected ? 'bg-sidebar-active' : ''}`}
            >
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={checkedIds.has(d.deviceId)}
                  onCheckedChange={() => onToggleCheck(d.deviceId)}
                  aria-label={`Select ${dev?.deviceName ?? d.deviceId}`}
                  data-testid="agent-device-check"
                />
              </TableCell>
              <TableCell className="text-center">
                <AgentDeviceStatusDot status={effectiveAgentDeviceStatus(d, liveStatusByUuid)} />
              </TableCell>
              <TableCell
                className="font-medium truncate"
                title={d.found ? (dev?.deviceName ?? undefined) : undefined}
              >
                {d.found ? (
                  <span className="flex items-center gap-2">
                    <span className="truncate">{dash(dev?.deviceName)}</span>
                    {!d.isTestingDevice ? (
                      <Badge
                        variant="outline"
                        className="shrink-0 border-warning/40 text-warning"
                        title={TESTING_DEVICE_REQUIRED_MESSAGE}
                        data-testid="agent-device-not-testing"
                      >
                        {'Not testing account'}
                      </Badge>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-muted-foreground italic">{'Device not found'}</span>
                )}
              </TableCell>
              <TableCell
                className="truncate"
                title={d.found ? (dev?.osVersion ?? undefined) : undefined}
              >
                {d.found ? dash(dev?.osVersion) : '—'}
              </TableCell>
              <TableCell
                className="truncate"
                title={d.found ? (dev?.localAppVersion ?? undefined) : undefined}
              >
                {d.found ? dash(dev?.localAppVersion) : '—'}
              </TableCell>
              <TableCell
                className="truncate"
                title={d.found ? (d.currentAssignOwnerEmail ?? undefined) : undefined}
              >
                {d.found ? dash(d.currentAssignOwnerEmail) : '—'}
              </TableCell>
              <TableCell>
                {d.found && d.currentType ? (
                  <Badge variant="secondary">{d.currentType}</Badge>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell
                className="truncate"
                title={d.found ? (d.currentAssignName ?? undefined) : undefined}
              >
                {d.found ? dash(d.currentAssignName) : '—'}
              </TableCell>
              <TableCell
                className="font-mono text-xs truncate"
                title={d.found ? (d.currentAssignId ?? undefined) : d.deviceId}
              >
                {d.found ? dash(d.currentAssignId) : <span className="font-sans">{d.deviceId}</span>}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  {d.found ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Update assignment"
                      data-testid="agent-device-edit-assign"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditAssign(d);
                      }}
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Remove device"
                    data-testid="agent-device-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(d);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
